/**
 * The facilitator's screen for Juurisyyt (admin.section of juurisyyt.v1):
 * the first deliberation's tally, the second deliberation (the discussion) and
 * the lock's state (specs/systemic-map-v2-room-review.md §2).
 *
 * The lock itself is the "Lukitse arviot" button in the flow control (the
 * systemic_v2.lock_judgements job); unlocking is here, with a reason.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import API_ENDPOINTS from '../../config/api';
import { useTranslation } from '../../hooks/useTranslation';
import useContentRefresh from '../../hooks/useContentRefresh';
import { v2Json } from './v2Fetch';
import { approvalLabel, earlierLabel } from './review';
import CardWhy from './CardWhy';
import SourceList from './SourceList';

const Wrap = styled.div`display: flex; flex-direction: column; gap: 12px; color: #e2e8f0;`;
const Bar = styled.div`display: flex; flex-wrap: wrap; gap: 8px 16px; font-size: 14px; color: #cbd5e1;`;
const Tabs = styled.div`display: flex; gap: 6px;`;
const Tab = styled.button`
  min-height: 40px; padding: 0 14px; border-radius: 999px; cursor: pointer; font-weight: 600; color: #e2e8f0;
  border: 1px solid ${(p) => (p.$on ? 'rgba(125,211,160,0.7)' : 'rgba(148,163,184,0.35)')};
  background: ${(p) => (p.$on ? 'rgba(125,211,160,0.14)' : 'transparent')};
`;
const Card = styled.div`
  border: 1px solid ${(p) => (p.$focus ? 'rgba(251,191,36,0.7)' : 'rgba(148,163,184,0.25)')};
  border-radius: 12px; padding: 12px; opacity: ${(p) => (p.$faded ? 0.55 : 1)};
  background: rgba(15,23,42,0.6);
`;
const Title = styled.div`font-weight: 600; font-size: 15px; line-height: 1.35; text-decoration: ${(p) => (p.$struck ? 'line-through' : 'none')};`;
const Meta = styled.div`font-size: 12px; color: #94a3b8; margin-top: 4px;`;
const Badge = styled.span`
  display: inline-block; margin-left: 8px; padding: 1px 8px; border-radius: 999px; font-size: 12px; font-weight: 600;
  background: ${(p) => ({ keep: 'rgba(16,185,129,0.2)', revise: 'rgba(56,189,248,0.2)', reject: 'rgba(248,113,113,0.2)' }[p.$o] || 'transparent')};
`;
const Row = styled.div`display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px;`;
const Btn = styled.button`
  min-height: 40px; padding: 0 12px; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 600;
  border: 1px solid ${(p) => (p.$danger ? 'rgba(248,113,113,0.5)' : 'rgba(148,163,184,0.4)')};
  background: ${(p) => (p.$primary ? 'rgba(16,185,129,0.2)' : 'transparent')}; color: #e2e8f0;
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;
const Area = styled.textarea`
  width: 100%; min-height: 70px; margin-top: 8px; padding: 8px; border-radius: 8px; font-size: 14px; font-family: inherit;
  border: 1px solid rgba(148,163,184,0.4); background: rgba(0,0,0,0.3); color: #e2e8f0;
`;
const Note = styled.div`font-size: 13px; color: ${(p) => (p.$error ? '#fca5a5' : '#cbd5e1')}; margin-top: 6px; white-space: pre-wrap;`;

function CardRow({ card, focused, locked, instanceId, onChanged }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(card.title);
  const [note, setNote] = useState('');
  const [ai, setAi] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const outcome = card.review?.outcome || null;
  const earlier = earlierLabel(card.earlier, t);

  const post = async (url, body) => {
    setBusy(true); setError(null);
    try {
      await v2Json(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ instanceId, ...body }) });
      await onChanged();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  // The AI moderates: from the votes and what the facilitator types, it names
  // the split and proposes a sharper wording. It reuses the refine dialogue.
  const askAi = async () => {
    setBusy(true); setError(null);
    try {
      const v = card.verdicts || {};
      const message = t('systemicMapV2.admin.moderatePrompt', {
        approve: v.approve || 0, reject: v.reject || 0, refine: v.refine || 0, note: note || '—',
      });
      const r = await v2Json(API_ENDPOINTS.SYSTEMIC_V2_JUURISYY_REFINE(), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instanceId, nodeId: card.id, participantMessage: message, history: [] }),
      });
      setAi(r);
      if (r.refined) { setDraft(r.refined); setEditing(true); }
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  return (
    <Card $focus={focused} $faded={outcome === 'reject' || card.culled}>
      <Title $struck={outcome === 'reject' || card.culled}>
        {card.title}
        {outcome && <Badge $o={outcome}>{t(`systemicMapV2.admin.outcomes.${outcome}`)}</Badge>}
        {card.culled && <Badge $o="reject">{t('systemicMapV2.admin.culled')}</Badge>}
      </Title>
      <Meta>
        {approvalLabel(card.verdicts, t)}
        {' · '}{t('systemicMapV2.admin.tally', card.verdicts)}
        {card.kipupiste ? ` · ${card.kipupiste}` : ''}{card.anchor ? ` → ${card.anchor}` : ''}
        {card.lensName ? ` · ${card.lensName}` : ''}
      </Meta>
      {/* What the este's authors think, apart from everyone else (#1249). */}
      {card.split ? (
        <Meta data-testid="split-tally">
          {t('systemicMapV2.admin.splitSelf', card.split.self)}{' · '}{t('systemicMapV2.admin.splitOutside', card.split.outside)}
        </Meta>
      ) : null}
      {earlier && <Meta>{earlier}</Meta>}
      {/* The card under discussion shows what it rests on (G5). */}
      {focused && <CardWhy rationale={card.rationale} verification={card.verification} />}
      {!card.culled && (
        <Row>
          <Btn type="button" disabled={busy} onClick={() => post(API_ENDPOINTS.SYSTEMIC_V2_REVIEW_FOCUS(), { nodeId: focused ? null : card.id })}>
            {focused ? t('systemicMapV2.admin.offWall') : t('systemicMapV2.admin.onWall')}
          </Btn>
          <Btn type="button" disabled={busy || locked} $primary={outcome === 'keep'}
            onClick={() => post(API_ENDPOINTS.SYSTEMIC_V2_REVIEW_OUTCOME(), { nodeId: card.id, outcome: outcome === 'keep' ? 'clear' : 'keep' })}>
            {t('systemicMapV2.admin.keep')}
          </Btn>
          <Btn type="button" disabled={busy || locked} onClick={() => setEditing((e) => !e)}>{t('systemicMapV2.admin.edit')}</Btn>
          <Btn type="button" disabled={busy || locked} $danger
            onClick={() => post(API_ENDPOINTS.SYSTEMIC_V2_REVIEW_OUTCOME(), { nodeId: card.id, outcome: outcome === 'reject' ? 'clear' : 'reject' })}>
            {outcome === 'reject' ? t('systemicMapV2.admin.undoReject') : t('systemicMapV2.admin.reject')}
          </Btn>
          <Btn type="button" disabled={busy || locked} onClick={askAi}>{t('systemicMapV2.admin.askAi')}</Btn>
        </Row>
      )}
      {/* The room talks about the card on the wall; that is where its words go. */}
      {!card.culled && !locked && (focused || ai) && (
        <Area value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('systemicMapV2.admin.notePlaceholder')} />
      )}
      {ai && (ai.question || ai.rationale) && <Note>{[ai.rationale, ai.question].filter(Boolean).join('\n')}</Note>}
      {ai?.sources?.length ? <SourceList sources={ai.sources} /> : null}
      {editing && !locked && (
        <>
          <Area value={draft} onChange={(e) => setDraft(e.target.value)} />
          <Row>
            <Btn type="button" $primary disabled={busy || !draft.trim() || draft.trim() === card.title}
              onClick={async () => { await post(API_ENDPOINTS.SYSTEMIC_V2_REVIEW_REVISE(), { nodeId: card.id, title: draft.trim(), rationale: ai?.rationale || note, sourceChunkIds: (ai?.sources || []).map((x) => x.chunkId), basis: ai?.basis }); setEditing(false); }}>
              {t('systemicMapV2.admin.saveVersion')}
            </Btn>
          </Row>
        </>
      )}
      {error && <Note $error>{error}</Note>}
    </Card>
  );
}

export default function JuurisyytAdmin({ instanceId }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('discussion');

  const load = useCallback(async () => {
    if (!instanceId) return;
    try {
      setData(await v2Json(API_ENDPOINTS.SYSTEMIC_V2_REVIEW(`kid=${instanceId}`)));
      setError(null);
    } catch (err) { setError(err.message); }
  }, [instanceId]);
  useEffect(() => { load(); }, [load]);
  useContentRefresh(instanceId, ['systemic_v2'], load, { minIntervalMs: 3000 });

  const byKipupiste = useMemo(() => {
    const m = new Map();
    for (const c of data?.cards || []) {
      const k = c.kipupiste || '—';
      const row = m.get(k) || { cards: 0, voted: 0, votes: 0 };
      row.cards += 1;
      if (c.approval) { row.voted += 1; row.votes += c.approval.votes; }
      m.set(k, row);
    }
    return [...m.entries()];
  }, [data]);

  // Unlocking asks for a reason in the panel itself; null = not asking.
  const [unlockReason, setUnlockReason] = useState(null);
  const unlock = async () => {
    const reason = unlockReason?.trim();
    if (!reason) return;
    try {
      await v2Json(API_ENDPOINTS.SYSTEMIC_V2_REVIEW_UNLOCK(), {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ instanceId, reason }),
      });
      setUnlockReason(null);
      await load();
    } catch (err) { setError(err.message); }
  };

  if (error) return <Note $error>{error}</Note>;
  if (!data) return <Note>{t('juurisyyV2.loading')}</Note>;
  const s = data.summary;
  return (
    <Wrap>
      <Bar>
        <span>{t('systemicMapV2.admin.summary', s)}</span>
        {data.locked
          ? <span>{t('systemicMapV2.admin.lockedBy', { by: data.lockedBy || '—', at: new Date(data.lockedAt).toLocaleString() })} {unlockReason === null && <Btn type="button" onClick={() => setUnlockReason('')}>{t('systemicMapV2.admin.unlock')}</Btn>}</span>
          : <span>{t('systemicMapV2.admin.openHint')}</span>}
      </Bar>
      {data.locked && unlockReason !== null && (
        <div>
          <Area autoFocus value={unlockReason} placeholder={t('systemicMapV2.admin.unlockReason')}
            aria-label={t('systemicMapV2.admin.unlockReason')} onChange={(e) => setUnlockReason(e.target.value)} />
          <Row>
            <Btn type="button" $primary disabled={!unlockReason.trim()} onClick={unlock}>{t('systemicMapV2.admin.unlock')}</Btn>
            <Btn type="button" onClick={() => setUnlockReason(null)}>{t('common.cancel')}</Btn>
          </Row>
        </div>
      )}
      <Tabs>
        <Tab type="button" $on={tab === 'swipe'} onClick={() => setTab('swipe')}>{t('systemicMapV2.admin.swipeTab')}</Tab>
        <Tab type="button" $on={tab === 'discussion'} onClick={() => setTab('discussion')}>{t('systemicMapV2.admin.discussionTab')}</Tab>
      </Tabs>
      {tab === 'swipe' && byKipupiste.map(([k, r]) => (
        <Card key={k}><Title>{k}</Title><Meta>{t('systemicMapV2.admin.deckTally', r)}</Meta></Card>
      ))}
      {tab === 'discussion' && data.cards.map((c) => (
        <CardRow key={c.id} card={c} focused={data.focusNodeId === c.id} locked={data.locked} instanceId={instanceId} onChanged={load} />
      ))}
    </Wrap>
  );
}
